import type { Meeting, MeetingDateRange } from './meetingTypes.ts';

const hoursAgo = (h: number): string => new Date(Date.now() - h * 60 * 60 * 1000).toISOString();
const daysAgo = (d: number): string => new Date(Date.now() - d * 24 * 60 * 60 * 1000).toISOString();

const OMAR = {
	accountId: 'aaid-omar',
	displayName: 'Omar Darboe',
	avatarUrl: 'https://api.dicebear.com/7.x/initials/svg?seed=Omar%20Darboe',
};

/**
 * Fixture meetings for the mock hook. Timestamps are relative to "now" so they
 * fall into the Today / Yesterday / This week buckets when grouped. Includes a
 * mix of action items with and without a 1-click CTA payload to exercise the
 * swappable ActionItem boundary.
 */
const ALL_MOCK_MEETINGS: Meeting[] = [
	{
		id: 'ari:mock:meeting/1',
		title: 'Use context from your meetings in Confluence',
		startsAt: hoursAgo(3),
		recorderId: OMAR.accountId,
		owner: OMAR,
		invitees: [OMAR],
		// NOTE: real, resolvable Loom video so the new-tab fallback opens an actual
		// recording during local testing. Other entries remain placeholders.
		videoAri:
			'ari:cloud:loom:a436116f-02ce-4520-8fbb-7301462a1674:video/activation/1621dc1f-3702-4525-84ba-fad829566798/aa417397344b45cd91192f4dfab52ad7',
		videoUrl: 'https://www.loom.com/share/0f1a6ac5510a41c78f09595855b6ea86',
		notesPageUrl: 'https://hello.atlassian.net/wiki/spaces/DEMO/pages/1',
		actionItems: [
			{
				id: 'ai-1a',
				content: 'Turn your H2 top priority into a PRD',
				timestampSeconds: 125,
				action: { label: 'Create PRD', key: 'confluence-page-create' },
			},
			{
				id: 'ai-1b',
				content: 'Turn 3 action items into Jira issues',
				timestampSeconds: 340,
				action: { label: 'Create Jira items', key: 'jira-issue-create' },
			},
			{
				id: 'ai-1c',
				content: 'Visualize your brainstorm in a whiteboard',
				timestampSeconds: 512,
				// No action payload → renders as plain text.
			},
		],
	},
	{
		id: 'ari:mock:meeting/2',
		title: 'Meetings tab catch up',
		startsAt: daysAgo(1),
		recorderId: OMAR.accountId,
		owner: OMAR,
		invitees: [OMAR],
		videoAri: 'ari:mock:video/2',
		videoUrl: 'https://www.loom.com/share/mock2',
		notesPageUrl: 'https://hello.atlassian.net/wiki/spaces/DEMO/pages/2',
		actionItems: [
			{
				id: 'ai-2a',
				content: 'Turn your H2 top priority into a PRD',
				timestampSeconds: 90,
				action: { label: 'Create PRD', key: 'confluence-page-create' },
			},
			{
				id: 'ai-2b',
				content: 'Visualize your brainstorm in a whiteboard',
				timestampSeconds: 220,
			},
		],
	},
	{
		id: 'ari:mock:meeting/3',
		title: 'Weekly sync catch up',
		startsAt: daysAgo(3),
		recorderId: OMAR.accountId,
		owner: OMAR,
		invitees: [OMAR],
		videoAri: 'ari:mock:video/3',
		videoUrl: 'https://www.loom.com/share/mock3',
		actionItems: [
			{
				id: 'ai-3a',
				content: 'Follow up with design on the final mocks',
				timestampSeconds: 60,
			},
		],
	},
];

const inRange = (isoDate: string, range: MeetingDateRange): boolean => {
	const t = new Date(isoDate).getTime();
	return t >= new Date(range.rangeStart).getTime() && t <= new Date(range.rangeEnd).getTime();
};

/**
 * Returns mock meetings, optionally filtered to a date range (simulating the
 * `rangeStart`/`rangeEnd` args on `loom_connectedCalendars.meetings`).
 */
export const getMockMeetings = (dateRange?: MeetingDateRange): Meeting[] => {
	if (!dateRange) {
		return ALL_MOCK_MEETINGS;
	}
	return ALL_MOCK_MEETINGS.filter((meeting) => inRange(meeting.startsAt, dateRange));
};
